import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Select,
    VStack,
    HStack,
    Switch,
    Text,
    useToast,
    useColorModeValue,
} from "@chakra-ui/react";

import { Exercise, exerciseService } from "services/exerciseService";

interface EditExerciseModalProps {
    isOpen: boolean;
    onClose: () => void;
    exercise: Exercise | null;
    onSuccess: () => void;
}

export default function EditExerciseModal({
    isOpen,
    onClose,
    exercise,
    onSuccess,
}: EditExerciseModalProps) {
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false);

    // Chakra Color Mode
    const bgColor = useColorModeValue("white", "navy.800");
    const textColor = useColorModeValue("navy.700", "white");
    const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
    const inputBg = useColorModeValue("white", "navy.700");

    const [formData, setFormData] = useState({
        name: "",
        description: "",
        category: "",
        muscle_group: "",
        equipment: "",
        instructions: "",
        image_url: "",
        is_public: false,
    });

    // Categories and options
    const categories = ["strength", "cardio", "flexibility", "sports", "balance", "coordination"];
    const muscleGroups = ["chest", "back", "shoulders", "arms", "legs", "core", "glutes", "full body"];
    const equipment = ["bodyweight", "dumbbells", "barbell", "kettlebell", "resistance_bands", "machine", "cable", "other"];

    useEffect(() => {
        if (exercise && isOpen) {
            setFormData({
                name: exercise.name || "",
                description: exercise.description || "",
                category: exercise.category || "",
                muscle_group: exercise.muscle_group || "",
                equipment: exercise.equipment || "",
                instructions: exercise.instructions || "",
                image_url: exercise.image_url || "",
                is_public: exercise.is_public || false,
            });
        }
    }, [exercise, isOpen]);

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = async () => {
        if (!exercise || !formData.name.trim() || !formData.category || !formData.muscle_group || !formData.equipment) {
            toast({
                title: "Validation Error",
                description: "Please fill in all required fields",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsLoading(true);
        try {
            await exerciseService.updateExercise(exercise.id, {
                name: formData.name.trim(),
                description: formData.description.trim(),
                category: formData.category,
                muscle_group: formData.muscle_group,
                equipment: formData.equipment,
                instructions: formData.instructions.trim(),
                image_url: formData.image_url.trim(),
                is_public: formData.is_public,
            });

            toast({
                title: "Success",
                description: "Exercise updated successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            onSuccess();
            handleClose();
        } catch (error) {
            console.error("Error updating exercise:", error);
            toast({
                title: "Error",
                description: "Failed to update exercise. Please try again.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: "",
            description: "",
            category: "",
            muscle_group: "",
            equipment: "",
            instructions: "",
            image_url: "",
            is_public: false,
        });
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="xl">
            <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
            <ModalContent bg={bgColor} borderRadius="20px" border="1px solid" borderColor={borderColor}>
                <ModalHeader color={textColor} fontWeight="700" fontSize="22px">
                    Edit Exercise
                </ModalHeader>
                <ModalCloseButton color={textColor} />
                <ModalBody>
                    <VStack spacing={5}>
                        {/* Basic Information */}
                        <FormControl isRequired>
                            <FormLabel color={textColor} fontWeight="500">
                                Exercise Name
                            </FormLabel>
                            <Input
                                placeholder="Enter exercise name"
                                value={formData.name}
                                onChange={(e) => handleInputChange("name", e.target.value)}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={textColor}
                                _placeholder={{ color: secondaryTextColor }}
                                _focus={{
                                    borderColor: "brand.500",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                }}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel color={textColor} fontWeight="500">
                                Description
                            </FormLabel>
                            <Textarea
                                placeholder="Brief description of the exercise"
                                value={formData.description}
                                onChange={(e) => handleInputChange("description", e.target.value)}
                                rows={3}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={textColor}
                                _placeholder={{ color: secondaryTextColor }}
                                _focus={{
                                    borderColor: "brand.500",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                }}
                            />
                        </FormControl>

                        {/* Categories */}
                        <HStack spacing={5} w="100%">
                            <FormControl isRequired>
                                <FormLabel color={textColor} fontWeight="500">
                                    Category
                                </FormLabel>
                                <Select
                                    placeholder="Select category"
                                    value={formData.category}
                                    onChange={(e) => handleInputChange("category", e.target.value)}
                                    bg={inputBg}
                                    borderColor={borderColor}
                                    color={textColor}
                                    _focus={{
                                        borderColor: "brand.500",
                                        boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                    }}
                                >
                                    {categories.map((category) => (
                                        <option key={category} value={category}>
                                            {category.charAt(0).toUpperCase() + category.slice(1)}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel color={textColor} fontWeight="500">
                                    Muscle Group
                                </FormLabel>
                                <Select
                                    placeholder="Select muscle group"
                                    value={formData.muscle_group}
                                    onChange={(e) => handleInputChange("muscle_group", e.target.value)}
                                    bg={inputBg}
                                    borderColor={borderColor}
                                    color={textColor}
                                    _focus={{
                                        borderColor: "brand.500",
                                        boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                    }}
                                >
                                    {muscleGroups.map((muscle) => (
                                        <option key={muscle} value={muscle}>
                                            {muscle.charAt(0).toUpperCase() + muscle.slice(1)}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </HStack>

                        <FormControl isRequired>
                            <FormLabel color={textColor} fontWeight="500">
                                Equipment
                            </FormLabel>
                            <Select
                                placeholder="Select equipment"
                                value={formData.equipment}
                                onChange={(e) => handleInputChange("equipment", e.target.value)}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={textColor}
                                _focus={{
                                    borderColor: "brand.500",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                }}
                            >
                                {equipment.map((equip) => (
                                    <option key={equip} value={equip}>
                                        {equip.charAt(0).toUpperCase() + equip.slice(1)}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Instructions */}
                        <FormControl>
                            <FormLabel color={textColor} fontWeight="500">
                                Instructions
                            </FormLabel>
                            <Textarea
                                placeholder="Step-by-step instructions for performing the exercise"
                                value={formData.instructions}
                                onChange={(e) => handleInputChange("instructions", e.target.value)}
                                rows={4}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={textColor}
                                _placeholder={{ color: secondaryTextColor }}
                                _focus={{
                                    borderColor: "brand.500",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                }}
                            />
                        </FormControl>

                        {/* Image URL */}
                        <FormControl>
                            <FormLabel color={textColor} fontWeight="500">
                                Image URL (Optional)
                            </FormLabel>
                            <Input
                                placeholder="https://example.com/exercise-image.jpg"
                                value={formData.image_url}
                                onChange={(e) => handleInputChange("image_url", e.target.value)}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={textColor}
                                _placeholder={{ color: secondaryTextColor }}
                                _focus={{
                                    borderColor: "brand.500",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                }}
                            />
                        </FormControl>

                        {/* Public Toggle */}
                        <FormControl>
                            <HStack justify="space-between" w="100%">
                                <VStack align="flex-start" spacing={0}>
                                    <FormLabel color={textColor} fontWeight="500" mb={0}>
                                        Make Public
                                    </FormLabel>
                                    <Text fontSize="14px" color={secondaryTextColor}>
                                        Allow other trainers to use this exercise
                                    </Text>
                                </VStack>
                                <Switch
                                    isChecked={formData.is_public}
                                    onChange={(e) => handleInputChange("is_public", e.target.checked)}
                                    colorScheme="brand"
                                />
                            </HStack>
                        </FormControl>
                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <Button 
                        mr={3} 
                        onClick={handleClose}
                        variant="ghost"
                        color={textColor}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        isLoading={isLoading}
                        loadingText="Updating..."
                        colorScheme="brand"
                    >
                        Update Exercise
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
